import Link from 'next/link';
import { useRouter } from 'next/router';
import { memo, useCallback } from 'react';

import { useTrackSidebarInteraction } from '@air/analytics';
import { cn } from '@air/utils-tailwind';

import { type SearchNavItem } from '~/components/v2/components/workspace/search-nav/searchNavItems';
import { SubSideNavItemLabel } from '~/components/v2/components/workspace/sub-side-nav/item/SubSideNavItemLabel';
import { SubSideNavItemPrefix } from '~/components/v2/components/workspace/sub-side-nav/item/SubSideNavItemPrefix';
import { WorkspaceSidebarNavigationItem } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarNavigationItem';
import { getCanvasQueryParams } from '~/constants/canvasQueryParams';

export type SearchSubSideNavItemProps = {
  item: SearchNavItem;
  className?: string;
};

export const SearchSubSideNavItem = memo(({ item, className }: SearchSubSideNavItemProps) => {
  const { pathname } = useRouter();
  const { trackSidebarInteraction } = useTrackSidebarInteraction();
  const Icon = item.icon;
  const isSelected = pathname === item.href || pathname.startsWith(`${item.href}/`);

  const handleClick = useCallback(() => {
    trackSidebarInteraction({ action: 'link_click', type: item.trackingId });
  }, [item.trackingId, trackSidebarInteraction]);

  return (
    <WorkspaceSidebarNavigationItem
      appearance="subNav"
      asChild
      className={cn('h-8 px-2.5', className)}
      data-item-id={item.id}
      data-selected={isSelected}
      data-testid="SUB_SIDE_NAV_ITEM"
    >
      <Link href={{ pathname: item.href, query: getCanvasQueryParams() }} onClick={handleClick}>
        <SubSideNavItemPrefix className="text-gray-10 group-data-[selected=true]/workspace-sidebar-navigation-item:text-gray-12">
          <Icon color="currentColor" size="sm" />
        </SubSideNavItemPrefix>
        <SubSideNavItemLabel>{item.label}</SubSideNavItemLabel>
      </Link>
    </WorkspaceSidebarNavigationItem>
  );
});

SearchSubSideNavItem.displayName = 'SearchSubSideNavItem';
